<template>
  <div v-if="attributeId"
       class="product-carousel__wrapper">
    <template v-if="loading">
      <loading-spinner size="3x"></loading-spinner>
    </template>
    <template v-else>
      <transition-group-fade id="product-carousel"
                             class="product-carousel"
                             :entrance-delay="0.1"
                             :entrance-delay-offset="0.04"
                             hide-leave-transition
                             intersect>
        <div v-for="(item, index) in internalProducts"
             :key="item.name"
             :data-index="index"
             class="product-carousel__item">
          <div class="product-carousel__item-image">
            <img :src="getProductImageUrl(item)"
                 :alt="getProductImageAlt(item)">
          </div>
          <div class="product-carousel__item-name">
            {{ item.displayName }}
          </div>

          <processing-button class="btn btn-accent-1 add-to-basket"
                             :action="() => addToBasket(item)">
            <template #default>
              <span class="add-to-basket__wrapper">
                <div class="add-to-basket__price">
                  {{ getItemPrice(item) }}
                </div>
                <font-awesome-icon key="add"
                                   class="icon"
                                   :icon="['fal', 'shopping-basket']"></font-awesome-icon>
                Buy Now
              </span>
            </template>
            <template #success>
              <span>
                Added
              </span>
            </template>
          </processing-button>
        </div>
      </transition-group-fade>
      <a v-if="canSlideLeft"
         class="product-carousel__slide-button left"
         :class="{'product-carousel__slide-button--disabled' : !canSlideLeft}"
         @click="onPrev">
        <font-awesome-icon :icon="['fas','chevron-circle-left']"></font-awesome-icon>
      </a>
      <a v-if="canSlideRight"
         class="product-carousel__slide-button right"
         :class="{'product-carousel__slide-button--disabled' : !canSlideRight}"
         @click="onNext">
        <font-awesome-icon :icon="['fas','chevron-circle-right']"></font-awesome-icon>
      </a>

      <div v-if="showButtons"
           class="product-carousel__button-container">
        <button v-scroll-to="'#product-carousel'"
                class="btn btn-accent-1"
                :disabled="!canSlideLeft"
                @click="onPrev">
          Prev
        </button>
        <button v-scroll-to="'#product-carousel'"
                class="btn btn-accent-1"
                :disabled="!canSlideRight"
                @click="onNext">
          Next
        </button>
      </div>
    </template>

    <b-toast id="product-added"
             toaster="b-toaster-bottom-right"
             header-class="toast-header"
             solid>
      <template #toast-title>
        Product Added
      </template>
      <div class="product-toast__text">
        <b>{{ lastProductAdded }}</b> has been
        added to your basket
      </div>
      <a :href="basketUrl"
         class="btn btn-accent-1">
        Go to your basket
        <font-awesome-icon class="icon"
                           :icon="['fal', 'shopping-basket']"></font-awesome-icon>
      </a>
    </b-toast>

    <template v-if="errorMessage">
      <default-modal id="error-modal"
                     close-only>
        <template #heading>
          Warning
        </template>
        <template #content>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="errorMessage" />
        </template>
      </default-modal>
    </template>
  </div>
</template>

<script>
import { EcommerceModule, BasketResource } from "spiritweb.packages.ecommerce";
import { basket } from "~/Scripts/config/constants";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";
import TransitionGroupFade from "~/Components/TransitionGroupFade.vue";
import { listProducts } from "~/Scripts/services/productService";
import ProcessingButton from "~/Components/Base/ProcessingButton.vue";
import DefaultModal from "~/Components/Base/DefaultModal.vue";

export default {
  name: "ProductCarousel",
  components: {
    TransitionGroupFade,
    LoadingSpinner,
    ProcessingButton,
    DefaultModal,
  },
  props: {
    imageUrl: {
      type: String,
      default: null,
    },
    attributeId: {
      type: String,
      default: null,
    },
    basketUrl: {
      type: String,
      default: "/",
    },
  },
  data () {
    return {
      loading: false,
      errorMessage: null,
      scrollIndex: 0,
      slideCount: 5,
      products: null,
      lastProductAdded: null,
    };
  },
  computed: {
    internalProducts () {
      let result = [];

      if ( this.products && this.products.length > 0 ) {
        const end = Math.min( this.products.length,
          ( this.scrollIndex * this.slideCount ) + this.slideCount );
        result = this.products.slice( this.scrollIndex * this.slideCount, end );
      }

      return result;
    },
    canSlideLeft () {
      return this.showButtons && this.scrollIndex * this.slideCount >= this.slideCount;
    },
    canSlideRight () {
      return this.showButtons
      && this.scrollIndex * this.slideCount < this.products.length / this.slideCount;
    },
    showButtons () {
      return this.products && this.products.length > this.slideCount;
    },
  },
  mounted () {
    if ( this.attributeId ) {
      this.loading = true;

      const image = new Image();
      image.src = this.imageUrl;

      listProducts( this.attributeId )
        .then( ( result ) => {
          if ( result && result.data ) {
            this.products = result.data;
          }
        } )
        .finally( () => {
          this.loading = false;
        } );
    }
  },
  methods: {
    addToBasket ( product ) {
      const payload = {
        id: product.productId,
        quantity: 1,
      };

      this.$root.$emit( basket.addToBasket.REQUEST, payload );

      const quantity = 1;
      this.errorMessage = null;

      let success = false;

      return BasketResource.addProduct( product.productId, quantity )
        .then( ( result ) => {
          if ( result && result.data && result.data.length > 0 ) {
            success = result.data[0].orderLineResult.errorCode === 100;
            const error = result.data[0].orderLineResult.errorMessage;

            if ( !success ) {
              throw new Error( error );
            } else {
              this.$root.$emit( basket.addToBasket.SUCCESS, payload );

              const item = this.internalProducts.find( p => p.Id === result.data[0].id );
              if ( item && item.productPrices ) {
                result.data[0].price = item.productPrices[0].priceExcVat;
              }

              this.$gtag.onAddToBasket( result.data );
            }
          } else {
            throw new Error( result?.data ?? "Unable to add product to basket." );
          }
        } )
        .catch( ( error ) => {
          this.$root.$emit( basket.addToBasket.FAILURE, error );

          let message = null;

          if ( error && typeof error === "object" ) {
            message = error.message;
          } else {
            message = error;
          }

          this.errorMessage = this.cleanMessage( message ) ?? "Unable to add product to basket.";
          throw error;
        } )
        .finally( () => {
          this.$root.$emit( basket.addToBasket.RESPONSE, payload );
          if ( !success ) {
            this.$bvModal.show( "error-modal" );
          } else {
            this.lastProductAdded = product.displayName;
            this.$bvToast.show( "product-added" );
          }
        } );
    },
    cleanMessage ( message ) {
      let result = message;
      if ( result && result.startsWith( "Error: " ) ) {
        result = result.substring( 6 );
      }
      return result;
    },
    getProductImageUrl ( product ) {
      let result = this.imageUrl;

      if ( product.productImages && product.productImages.length > 0 ) {
        const image = product.productImages.find( p => p.type === "PRODUCT" );

        if ( image ) {
          result = image.uri;
        }
      }

      return result;
    },
    getProductImageAlt ( product ) {
      let result = this.imageUrl;

      if ( product.productImages && product.productImages.length > 0 ) {
        const image = product.productImages.find( p => p.type === "PRODUCT" );

        if ( image ) {
          result = image.altText;
        }
      }

      return result;
    },
    getItemPrice ( item ) {
      let result = null;

      if ( item && item.productPrices && item.productPrices.length > 0 ) {
        result = EcommerceModule.renderPrice( item.productPrices[0].priceExcVat, "GBP", "en-GB" );
      }

      return result;
    },
    onPrev () {
      if ( this.scrollIndex - 1 >= 0 ) {
        this.scrollIndex -= 1;
      }
    },
    onNext () {
      if ( this.scrollIndex + 1 <= this.products.length - this.slideCount ) {
        this.scrollIndex += 1;
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.product-carousel {
  padding: 60px 0;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  overflow: hidden;
  margin-bottom: -30px;
  width: 100%;

  &__wrapper {
    position: relative;
    min-height: 500px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    /deep/ .loading-spinner {
      padding: 100px 0;
    }
    /deep/ .loading-spinner__icon {
      color: @color-accent-1;
    }
  }

  &__slide-button {
    font-size: 36px;
    position: absolute;
    top: 50%;

    @media ( max-width: 767px ) {
      display: none;
    }

    &:hover {
      cursor: pointer;
    }

    &--disabled {
      color: @color-accent-1-dark;

      &:hover {
        cursor: default;
      }
    }

    &.left {
      left: -25px;
    }

    &.right {
      right: -25px;
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-grow: 1;
    flex-basis: 20%;
    margin-bottom: 30px;

    @media ( max-width: 1290px ) {
      flex-basis: 33.3%;
    }

    &-image {
      margin-bottom: 10px;
      & > img {
        max-height: 263px;
      }
    }

    &-name {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 10px;
      text-align: center;
      padding: 0 10px;
    }

    .btn {
      margin-top: auto;
    }
  }

  &__button-container {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    padding-bottom: 40px;

    @media ( min-width: 767px ) {
      display: none;
    }

    & > .btn {
      margin: 0 10px;
      flex-basis: 50%;
    }
  }

  .add-to-basket {
    min-width: 205px;
    min-height: 38px;

    display: flex;
    justify-content: center;
    align-items: center;

    &__wrapper {
      display: flex;
      align-items: center;
    }

    &__price {
      border-right: 2px solid #fff;
      padding-right: 10px;
    }

    .icon {
      margin-right: 5px;
    }
  }
}

.b-toast {
  right: 20px !important;
  bottom: 6px !important;
}

/deep/ .product-toast__text {
  margin-bottom: 10px;
}

/deep/ .toast-header {
  background-color: @color-accent-1;
  color: #fff;
  font-weight: 500;

  .close {
    color: #fff;
  }
}
</style>
